import { resetPassword } from "api/authentication";
import { TextField } from "components/inputs";
import PasswordRequirements from "components/PasswordRequirements";
import { PasswordValidatorRef } from "components/PasswordRequirements/interfaces";
import { Form, Formik } from "formik";
import useLocalizedRoutes from "hooks/useLocalizedRoutes";
import useQueryParams from "hooks/useQueryParams";
import { errorToast, Heading, successToast } from "iparque-components";
import { AppContext } from "providers/AppProvider";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import FormButton from "../FormButton";
import { getValidationSchema, initialValues } from "./configs";
import {
  PasswordDefinitionFormValues,
  PasswordDefinitionUrlParams,
} from "./interfaces";

const PasswordDefinitionForm: React.FC = () => {
  const { t } = useTranslation();
  const { setIsLoading } = useContext(AppContext);
  const { urlParams } = useQueryParams<PasswordDefinitionUrlParams>();
  const navigate = useNavigate();
  const passwordRef = useRef<PasswordValidatorRef>(null);
  const [isUrlValid, setIsUrlValid] = useState(false);
  const { getLanguageRoute } = useLocalizedRoutes();

  useEffect(() => {
    if (!urlParams("email") || !urlParams("hash")) {
      errorToast(
        t("7847") /* Erro! */,
        t("7878") /* O url inserido não está correto. */,
        { autoClose: false }
      );

      return;
    }

    setIsUrlValid(true);
  }, [t, urlParams]);

  const onSubmitHandler = async (values: PasswordDefinitionFormValues) => {
    const email = urlParams("email");
    const hash = urlParams("hash");

    if (!email || !hash) {
      return;
    }

    setIsLoading(true);

    const { success } = await resetPassword(email, hash, values.newPassword);

    setIsLoading(false);

    if (success) {
      successToast(
        t("586" /* Sucesso */),
        t("8162") /* Password alterada com sucesso */
      );
      navigate(getLanguageRoute("login"));
    } else {
      errorToast(
        t("7847") /* Erro! */,
        t("7877") /* Ocorreu um erro ao definir a nova senha. */
      );
    }
  };

  return (
    <>
      <Heading className="mt-20" variant="h8">
        {t("7880") /* Por favor, indica a tua nova senha de acesso */}
      </Heading>
      <Heading className="mb-20" variant="h7">
        {urlParams("email") || ""}
      </Heading>
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(t, passwordRef)}
        onSubmit={onSubmitHandler}
      >
        {({ values }) => (
          <Form>
            <TextField
              type="password"
              name="newPassword"
              label={t("7909") /* Nova Senha */}
              disabled={!isUrlValid}
            />
            <TextField
              type="password"
              name="confirmedNewPassword"
              label={t("7884") /* Confirmar Senha */}
              disabled={!isUrlValid}
            />
            <PasswordRequirements
              className="mt-20"
              ref={passwordRef}
              password={values.newPassword}
            />
            <FormButton type="submit" disabled={!isUrlValid}>
              {t("88") /* Guardar */}
            </FormButton>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PasswordDefinitionForm;
